import { useEffect, useRef, useState } from 'react';
import Prism from 'prismjs';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';
import { Check, Copy } from 'lucide-react';

interface CodeBlockProps {
  code: string;
  language: string;
  title?: string;
  showLineNumbers?: boolean;
}

export function CodeBlock({ code, language, title, showLineNumbers = false }: CodeBlockProps) {
  const codeRef = useRef<HTMLElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (codeRef.current) {
      Prism.highlightElement(codeRef.current);
    }
  }, [code, language]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lines = code.split('\n');

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-900 my-4">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
          <span className="text-sm font-medium text-slate-300">{title}</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-400 hover:text-white transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}
      <div className="relative">
        {!title && (
          <button
            onClick={handleCopy}
            className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 rounded transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
        <pre className="p-4 overflow-x-auto text-sm">
          {showLineNumbers ? (
            <code className="flex">
              <span className="pr-4 text-slate-600 select-none border-r border-slate-700 mr-4">
                {lines.map((_, i) => (
                  <span key={i} className="block text-right">
                    {i + 1}
                  </span>
                ))}
              </span>
              <code ref={codeRef} className={`language-${language}`}>
                {code}
              </code>
            </code>
          ) : (
            <code ref={codeRef} className={`language-${language}`}>
              {code}
            </code>
          )}
        </pre>
      </div>
    </div>
  );
}

interface TabbedCodeBlockProps {
  tabs: Array<{
    label: string;
    code: string;
    language: string;
  }>;
}

export function TabbedCodeBlock({ tabs }: TabbedCodeBlockProps) {
  const [activeTab, setActiveTab] = useState(0);
  const currentTab = tabs[activeTab];

  if (!currentTab) return null;

  return (
    <div className="rounded-lg overflow-hidden border border-slate-200 my-4">
      <div className="flex bg-slate-800 border-b border-slate-700">
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            onClick={() => setActiveTab(index)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === index ? 'text-white bg-slate-900' : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <CodeBlock code={currentTab.code} language={currentTab.language} />
    </div>
  );
}
